import React, { useState, useEffect, useRef } from "react";

type Container = {
  title: string;
  shape: "rectangular" | "circular" | "custom";
  width: number;
  height: number;
  backgroundColor: string;
  rows: number;
  columnsPerRow: number[];
  rowHeights: number[];
  gridGap?: number;
  borderRadius?: number;
};

const DesignContainerMakerPage: React.FC = () => {
  const [container, setContainer] = useState<Container>({
    title: "My Container",
    shape: "rectangular",
    width: 500,
    height: 500,
    backgroundColor: "#ffffff",
    columnsPerRow: [2, 2],
    rows: 2,
    rowHeights: [200, 200],
    gridGap: 16,
    borderRadius: 8,
  });

  const [cellBackgrounds, setCellBackgrounds] = useState<string[][]>(
    Array.from({ length: container.rows }, (_, rowIndex) =>
      Array.from({ length: container.columnsPerRow[rowIndex] || 1 }, () => ""),
    ),
  );

  const [rowWidths, setRowWidths] = useState<number[][]>(
    Array.from({ length: container.rows }, (_, rowIndex) =>
      Array.from(
        { length: container.columnsPerRow[rowIndex] || 1 },
        () => 100 / (container.columnsPerRow[rowIndex] || 1),
      ),
    ),
  );

  useEffect(() => {
    const savedBackgrounds = localStorage.getItem("bentoCellBackgrounds");
    if (savedBackgrounds) {
      try {
        const parsed = JSON.parse(savedBackgrounds);
        setCellBackgrounds(parsed);
      } catch (error) {
        console.error("Failed to parse saved cell backgrounds", error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "bentoCellBackgrounds",
      JSON.stringify(cellBackgrounds),
    );
  }, [cellBackgrounds]);

  const [activeCell, setActiveCell] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleRowResizeMouseDown = (e: React.MouseEvent, rowIndex: number) => {
    e.preventDefault();
    const startY = e.clientY;
    const initialHeight = container.rowHeights[rowIndex];

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newHeight = initialHeight + (moveEvent.clientY - startY);
      setContainer((prev) => {
        const newRowHeights = [...prev.rowHeights];
        newRowHeights[rowIndex] = newHeight > 50 ? newHeight : 50;
        return { ...prev, rowHeights: newRowHeights };
      });
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleCellResizeMouseDown = (
    e: React.MouseEvent,
    rowIndex: number,
    colIndex: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const initialWidth = rowWidths[rowIndex][colIndex];
    const initialNextWidth = rowWidths[rowIndex][colIndex + 1];

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaPercent = (deltaX / container.width) * 100;
      const newWidth = initialWidth + deltaPercent;
      const newNextWidth = initialNextWidth - deltaPercent;
      if (newWidth < 10 || newNextWidth < 10) return;
      setRowWidths((prev) => {
        const newRowWidths = [...prev];
        newRowWidths[rowIndex] = [...prev[rowIndex]];
        newRowWidths[rowIndex][colIndex] = newWidth;
        newRowWidths[rowIndex][colIndex + 1] = newNextWidth;
        return newRowWidths;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleExport = () => {
    console.log(container);
    alert("Exporting container...");
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Top Navigation */}
      <div className="flex justify-between items-center bg-gray-800 text-white p-4 shadow-md">
        <h1 className="text-xl">{container.title}</h1>
        <button
          onClick={handleExport}
          className="bg-blue-500 p-2 rounded-lg hover:bg-blue-600 transition"
        >
          Export
        </button>
      </div>

      <div className="flex flex-1">
        {/* Side Panel - Container Settings */}
        <div className="w-80 bg-white shadow-md p-4 overflow-auto">
          <h2 className="text-lg font-semibold mb-4">Container Settings</h2>
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">
              Columns per Row
            </label>
            {container.columnsPerRow.map((colCount, rowIndex) => (
              <div key={rowIndex} className="flex items-center gap-2 mb-2">
                <span className="text-sm w-12">Row {rowIndex + 1}</span>
                <input
                  type="number"
                  min={1}
                  value={colCount}
                  onChange={(e) => {
                    const newCount = +e.target.value;
                    const newColCount = [...container.columnsPerRow];
                    newColCount[rowIndex] = newCount;
                    setContainer({
                      ...container,
                      columnsPerRow: newColCount,
                    });
                    setRowWidths((prev) => {
                      const newRowWidths = [...prev];
                      newRowWidths[rowIndex] = Array.from(
                        { length: newCount },
                        () => 100 / newCount,
                      );
                      return newRowWidths;
                    });
                  }}
                  className="flex-1 p-1 border border-gray-300 rounded"
                />
              </div>
            ))}
          </div>

          {/* Columns and Rows */}
          <div className="mb-4">
            <label className="block text-sm mt-2 mb-2">Rows</label>
            <input
              type="number"
              value={container.rows}
              onChange={(e) => {
                const newRowCount = +e.target.value;
                const updatedColumnsPerRow = [...container.columnsPerRow];
                const updatedRowHeights = [...container.rowHeights];

                while (updatedColumnsPerRow.length < newRowCount) {
                  updatedColumnsPerRow.push(2);
                  updatedRowHeights.push(200);
                }
                updatedColumnsPerRow.length = newRowCount;
                updatedRowHeights.length = newRowCount;

                setContainer({
                  ...container,
                  rows: newRowCount,
                  columnsPerRow: updatedColumnsPerRow,
                  rowHeights: updatedRowHeights,
                });
                setCellBackgrounds((prev) =>
                  Array.from({ length: newRowCount }, (_, rowIndex) =>
                    Array.from(
                      { length: updatedColumnsPerRow[rowIndex] || 1 },
                      (_, colIndex) => prev[rowIndex]?.[colIndex] || "",
                    ),
                  ),
                );
                setRowWidths((prev) => {
                  const newRowWidths = [];
                  for (let rowIndex = 0; rowIndex < newRowCount; rowIndex++) {
                    const colCount = updatedColumnsPerRow[rowIndex] || 1;
                    if (rowIndex < prev.length) {
                      const oldRow = prev[rowIndex];
                      if (oldRow.length === colCount) {
                        newRowWidths.push(oldRow);
                      } else if (oldRow.length < colCount) {
                        newRowWidths.push([
                          ...oldRow,
                          ...Array.from(
                            { length: colCount - oldRow.length },
                            () => 100 / colCount,
                          ),
                        ]);
                      } else {
                        newRowWidths.push(oldRow.slice(0, colCount));
                      }
                    } else {
                      newRowWidths.push(
                        Array.from({ length: colCount }, () => 100 / colCount),
                      );
                    }
                  }
                  return newRowWidths;
                });
              }}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">
              Row Heights
            </label>
            {container.rowHeights.map((height, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <span className="text-sm w-12">Row {index + 1}</span>
                <input
                  type="number"
                  min={50}
                  value={height}
                  onChange={(e) => {
                    const updatedRowHeights = [...container.rowHeights];
                    updatedRowHeights[index] = +e.target.value;
                    setContainer({
                      ...container,
                      rowHeights: updatedRowHeights,
                    });
                  }}
                  className="flex-1 p-1 border border-gray-300 rounded"
                />
              </div>
            ))}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">
              Grid Gap (px)
            </label>
            <input
              type="number"
              min={0}
              value={container.gridGap || 0}
              onChange={(e) =>
                setContainer({ ...container, gridGap: +e.target.value })
              }
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">
              Border Radius (px)
            </label>
            <input
              type="number"
              min={0}
              value={container.borderRadius || 0}
              onChange={(e) =>
                setContainer({ ...container, borderRadius: +e.target.value })
              }
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 bg-white p-4 overflow-auto">
          {Array.from({ length: container.rows }).map((_, rowIndex) => {
            const rowHeight = container.rowHeights[rowIndex];
            const columns = container.columnsPerRow[rowIndex] || 1;
            return (
              <div
                key={`row-wrapper-${rowIndex}`}
                className="relative mb-4 group"
              >
                <div
                  key={`row-${rowIndex}`}
                  className="flex"
                  style={{
                    height: `${rowHeight}px`,
                    gap: `${container.gridGap}px`,
                    borderRadius: `${container.borderRadius}px`,
                  }}
                >
                  {Array.from({ length: columns }).map((_, colIndex) => (
                    <div
                      key={`cell-${rowIndex}-${colIndex}`}
                      className="relative group border border-gray-200 flex justify-center items-center"
                      style={{
                        width: `${rowWidths[rowIndex]?.[colIndex] || 100 / columns}%`,
                        backgroundColor: "transparent",
                        backgroundImage: cellBackgrounds[rowIndex]?.[colIndex]
                          ? `url(${cellBackgrounds[rowIndex][colIndex]})`
                          : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        borderRadius: `${container.borderRadius}px`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveCell({ row: rowIndex, col: colIndex });
                        fileInputRef.current?.click();
                      }}
                    >
                      {!cellBackgrounds[rowIndex]?.[colIndex] && (
                        <span>Add an asset</span>
                      )}
                      {colIndex < columns - 1 && (
                        <div
                          className="absolute top-0 right-0 h-full w-2 cursor-col-resize opacity-0 group-hover:opacity-100 transition"
                          onMouseDown={(e) =>
                            handleCellResizeMouseDown(e, rowIndex, colIndex)
                          }
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
                <div
                  className="h-2 bg-gray-300 cursor-row-resize opacity-0 group-hover:opacity-100 transition"
                  onMouseDown={(e) => handleRowResizeMouseDown(e, rowIndex)}
                ></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && activeCell) {
            const cell = { ...activeCell };
            const reader = new FileReader();
            reader.onload = () => {
              const updated = [...cellBackgrounds];
              updated[cell.row][cell.col] = reader.result as string;
              setCellBackgrounds(updated);
            };
            reader.readAsDataURL(file);
          }
          setActiveCell(null);
          (e.target as HTMLInputElement).value = "";
        }}
      />
    </div>
  );
};

export default DesignContainerMakerPage;
